import { Transaction } from "@/src/models/rewardly.model";
import { useTheme } from "@/src/styles/theme-provider";
import { ArrowDown2, ArrowRight, ArrowRotateLeft, ArrowUp2 } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import { DataTable } from "react-native-paper";
import ConfirmModal from "../../action-modals/confirm-modal";
import StateDisplay from "../../ui-kit/state-display";
import SnapshotAvatar from "../snapshot-avatar";

export type RecognitionRow = Transaction & { rewardValue: string };

type RecognitionsTableProps = Readonly<{
  transactions: RecognitionRow[];
  onDelete: (transactionId: string) => Promise<void>;
  isDeleting: boolean;
}>;

const EmptyState = () => (
  <StateDisplay
    descriptionLabel="LABEL_NO_RECOGNITIONS_FOUND"
    imgSource={require("@/src/assets/images/empty.png")}
  />
);

const SeeReasonToggle = ({ expanded, onPress }: { expanded: boolean; onPress: () => void }) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const Chevron = expanded ? ArrowUp2 : ArrowDown2;
  return (
    <Pressable
      onPress={onPress}
      className="flex-row items-center gap-1 md:border md:border-ash-3 rounded-lg px-2.5 py-1.5 active:opacity-70 self-start hover:bg-ash-3"
    >
      <Text className="text-xs text-slate">{t("LABEL_SEE_REASON")}</Text>
      <Chevron size={12} color={themeVars["--color-slate-default"] as string} />
    </Pressable>
  );
};

const RecognitionCard = ({
  transaction,
  onDeletePress,
}: {
  transaction: RecognitionRow;
  onDeletePress: () => void;
}) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  return (
    <View className="flex-col gap-3 p-4 bg-chalk rounded-xl md:border md:border-ash-3">
      <View className="flex-row items-center gap-2">
        <SnapshotAvatar user={transaction.senderInfo} size={28} />
        <Text className="text-sm font-medium text-onyx shrink" numberOfLines={1}>
          {transaction.senderInfo?.displayName}
        </Text>
        <ArrowRight size={14} color={themeVars["--color-ash-2"] as string} />
        <SnapshotAvatar user={transaction.recipientInfo} size={28} />
        <Text className="text-sm font-medium text-onyx flex-1" numberOfLines={1}>
          {transaction.recipientInfo?.displayName}
        </Text>
        <Pressable onPress={onDeletePress} hitSlop={8} className="active:opacity-70 p-1.5 hover:rounded-full hover:bg-ash-3">
          <ArrowRotateLeft size={18} color={themeVars["--color-crimson-default"] as string} />
        </Pressable>
      </View>

      <View className="bg-ash px-4 py-3 rounded-xl">
        <Text className="text-sm text-onyx italic">{`"${transaction.message ?? ""}"`}</Text>
      </View>

      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-3">
          <Text className="text-xs font-semibold text-primary">
            {transaction.amount} {t("LABEL_STARS_UNIT")}
          </Text>
          <Text className="text-xs font-semibold text-jade">{transaction.rewardValue}</Text>
        </View>
        <Text className="text-xs text-ash-2">
          {new Date(transaction.CreatedDate).toLocaleDateString()}
        </Text>
      </View>
    </View>
  );
};

const RecognitionsTable = ({ transactions, onDelete, isDeleting }: RecognitionsTableProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const toggleReason = (id: string) =>
    setExpandedId(previous => (previous === id ? null : id));

  const confirmDelete = async () => {
    if (!pendingDeleteId) return;
    await onDelete(pendingDeleteId);
    setPendingDeleteId(null);
  };

  const deleteModal = (
    <ConfirmModal
      visible={pendingDeleteId != null}
      title={t("QUEST_DELETE_RECOGNITION")}
      description={t("HINT_DELETE_RECOGNITION")}
      onConfirm={confirmDelete}
      onCancel={() => setPendingDeleteId(null)}
      loading={isDeleting}
    />
  );

  if (transactions.length === 0) {
    return <EmptyState />;
  }

  if (isMobile) {
    return (
      <View className="flex-col gap-3">
        {transactions.map((transaction) => (
          <RecognitionCard
            key={transaction.ItemId}
            transaction={transaction}
            onDeletePress={() => setPendingDeleteId(transaction.ItemId)}
          />
        ))}
        {deleteModal}
      </View>
    );
  }

  return (
    <View>
      <DataTable.Header style={{ borderBottomWidth: 1, borderBottomColor: "var(--color-ash-3)", elevation: 0 }}>
        <DataTable.Title style={{ flex: 1.5 }}>
          <Text className="text-xs text-onyx font-medium tracking-wide">{t("LABEL_SENDER")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1.5 }}>
          <Text className="text-xs text-onyx font-medium tracking-wide">{t("LABEL_RECIPIENT")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1, justifyContent: "center" }}>
          <Text className="text-xs text-onyx font-medium tracking-wide text-center">{t("LABEL_STARS_SENT")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1, justifyContent: "center" }}>
          <Text className="text-xs text-onyx font-medium tracking-wide text-center">{t("LABEL_REWARD")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1.2 }}>
          <Text className="text-xs text-onyx font-medium tracking-wide">{t("LABEL_REASON")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 1 }}>
          <Text className="text-xs text-onyx font-medium tracking-wide">{t("LABEL_DATE_SENT")}</Text>
        </DataTable.Title>
        <DataTable.Title style={{ flex: 0.6, justifyContent: "center" }}>
          <Text className="text-xs text-onyx font-medium tracking-wide text-center">{t("LABEL_ACTION")}</Text>
        </DataTable.Title>
      </DataTable.Header>

      {transactions.map((transaction) => (
        <View key={transaction.ItemId}>
          <DataTable.Row
            style={{
              borderBottomWidth: expandedId === transaction.ItemId ? 0 : 1,
              borderBottomColor: "var(--color-ash-3)",
              paddingVertical: 12,
            }}
          >
            <DataTable.Cell style={{ flex: 1.5 }}>
              <View className="flex-col">
                <Text className="text-onyx">{transaction.senderInfo?.displayName}</Text>
                <Text className="text-xs text-ash-2">{transaction.senderInfo?.email}</Text>
              </View>
            </DataTable.Cell>

            <DataTable.Cell style={{ flex: 1.5 }}>
              <View className="flex-col">
                <Text className="text-onyx">{transaction.recipientInfo?.displayName}</Text>
                <Text className="text-xs text-ash-2">{transaction.recipientInfo?.email}</Text>
              </View>
            </DataTable.Cell>

            <DataTable.Cell style={{ flex: 1, justifyContent: "center" }}>
              <Text className="font-semibold text-primary text-center">{transaction.amount}</Text>
            </DataTable.Cell>

            <DataTable.Cell style={{ flex: 1, justifyContent: "center" }}>
              <Text className="font-semibold text-jade text-center">{transaction.rewardValue}</Text>
            </DataTable.Cell>

            <DataTable.Cell style={{ flex: 1.2 }}>
              <SeeReasonToggle
                expanded={expandedId === transaction.ItemId}
                onPress={() => toggleReason(transaction.ItemId)}
              />
            </DataTable.Cell>

            <DataTable.Cell style={{ flex: 1 }}>
              <Text className="text-slate">
                {new Date(transaction.CreatedDate).toLocaleDateString()}
              </Text>
            </DataTable.Cell>

            <DataTable.Cell style={{ flex: 0.6, justifyContent: "center" }}>
              <Pressable
                onPress={() => setPendingDeleteId(transaction.ItemId)}
                hitSlop={8}
                className="active:opacity-70 p-1.5 hover:rounded-full hover:bg-ash-3"
              >
                <ArrowRotateLeft size={20} color={themeVars["--color-crimson-default"] as string} />
              </Pressable>
            </DataTable.Cell>
          </DataTable.Row>

          {expandedId === transaction.ItemId && (
            <View className="border-b border-ash-3 px-4 pb-3">
              <View className="self-start bg-chalk border border-ash-3 rounded-xl px-4 py-2.5 shadow-sm">
                <Text className="text-sm text-onyx italic">{`"${transaction.message ?? ""}"`}</Text>
              </View>
            </View>
          )}
        </View>
      ))}
      {deleteModal}
    </View>
  );
};

export default RecognitionsTable;
